import AntAuth from './AntAuth';
import AntTime from './AntTime';
import stringify from './stableStringify';

import hash from 'hash-sum';
import cs from './const.js';

const CACHE_PREFIX = 'CacheData-';
const CACHE_COMPRESSION_THRESHOLD = 64 * 1024;

function isQuotaExceededError(error) {
    return error?.name === 'QuotaExceededError' || error?.name === 'NS_ERROR_DOM_QUOTA_REACHED' || error?.code === 22 || error?.code === 1014;
}

function bytesToBase64(bytes) {
    let binary = '';
    const chunkSize = 0x8000;
    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
    }
    return btoa(binary);
}

function base64ToBytes(value) {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return bytes;
}

async function encodeCacheData(data) {
    if (
        typeof data !== 'string' ||
        data.length < CACHE_COMPRESSION_THRESHOLD ||
        typeof CompressionStream !== 'function' ||
        typeof Blob !== 'function' ||
        typeof Response !== 'function'
    ) {
        return { data };
    }

    try {
        const stream = new Blob([data]).stream().pipeThrough(new CompressionStream('gzip'));
        const compressed = new Uint8Array(await new Response(stream).arrayBuffer());
        const compressedBase64 = bytesToBase64(compressed);
        return compressedBase64.length < data.length
            ? { data: compressedBase64, encoding: 'gzip-base64' }
            : { data };
    } catch {
        return { data };
    }
}

async function decodeCacheData(data, encoding) {
    if (encoding !== 'gzip-base64') return data;
    if (typeof DecompressionStream !== 'function' || typeof Blob !== 'function' || typeof Response !== 'function') return null;

    try {
        const stream = new Blob([base64ToBytes(data)]).stream().pipeThrough(new DecompressionStream('gzip'));
        return await new Response(stream).text();
    } catch {
        return null;
    }
}

function clearExpiredStoredKeys(group) {
    const prefix = group ? `${CACHE_PREFIX}${group}-` : CACHE_PREFIX;
    const now = AntTime.now().unix();

    Object.keys(localStorage).forEach((key) => {
        if (!String(key).startsWith(prefix)) return;
        try {
            const cacheData = JSON.parse(localStorage.getItem(key));
            const expireAt = Number(cacheData?.expire_at);
            const isExpired = Number.isFinite(expireAt)
                ? expireAt <= 0 || expireAt < now
                : !cacheData?.queryAt || !AntTime.isUnixWithinToDay(cacheData.queryAt);
            if (isExpired) localStorage.removeItem(key);
        } catch {
            localStorage.removeItem(key);
        }
    });
}

function clearStoredGroup(group) {
    const prefix = `${CACHE_PREFIX}${group}-`;
    Object.keys(localStorage).forEach((key) => {
        if (String(key).startsWith(prefix)) localStorage.removeItem(key);
    });
}

export default {
    getSetting(name) {
        if (name === 'Sys_BaseLanguage') {
            let value = localStorage.getItem(cs.System_Code + '-Sys_BaseLanguage');
            if (value && value !== 'null' && value !== 'undefined') return value;
        }
        if (name === 'Sys_BaseCurrency') {
            let value = localStorage.getItem(cs.System_Code + '-Sys_BaseCurrency');
            if (value && value !== 'null' && value !== 'undefined') return value;
        }
        if (name === 'Sys_BaseCurrency_Show_Symbol') {
            let value = localStorage.getItem(cs.System_Code + '-Sys_BaseCurrency_Show_Symbol');
            if (value && value !== 'null' && value !== 'undefined') return parseInt(value);
        }
        var userSettings = AntAuth.getSessionInfo('user_settings');
        if (userSettings && userSettings[name] !== undefined) return userSettings[name];

        var busSettings = AntAuth.getSessionInfo('bus_settings');
        if (busSettings && busSettings[name] !== undefined) return busSettings[name];

        return null;
    },
    getBusinessSetting(name) {
        if (name === 'Sys_BaseLanguage') {
            let value = localStorage.getItem(cs.System_Code + '-Sys_BaseLanguage');
            if (value && value !== 'null' && value !== 'undefined') return value;
        }
        if (name === 'Sys_BaseCurrency') {
            let value = localStorage.getItem(cs.System_Code + '-Sys_BaseCurrency');
            if (value && value !== 'null' && value !== 'undefined') return value;
        }
        if (name === 'Sys_BaseCurrency_Show_Symbol') {
            let value = localStorage.getItem(cs.System_Code + '-Sys_BaseCurrency_Show_Symbol');
            if (value && value !== 'null' && value !== 'undefined') return parseInt(value);
        }
        var busSettings = AntAuth.getSessionInfo('bus_settings');
        if (busSettings && busSettings[name] !== undefined) return busSettings[name];

        return null;
    },
    getUserSetting(name) {
        var userSettings = AntAuth.getSessionInfo('user_settings');
        if (userSettings && userSettings[name] !== undefined) return userSettings[name];

        return null;
    },
    async setData(group, key, data, expireSecs) {
        var hashKey = hash(key);
        const storageKey = `${CACHE_PREFIX}${group}-${hashKey}`;
        const serializedData = stringify(data);
        if (typeof serializedData !== 'string') return false;
        const encoded = await encodeCacheData(serializedData);
        let cacheData = {
            data: encoded.data,
            encoding: encoded.encoding,
            cached_at: AntTime.now().unix(),
            expire_at: AntTime.now().add(expireSecs, 'seconds').unix()
        };
        const storageValue = stringify(cacheData);

        try {
            localStorage.setItem(storageKey, storageValue);
            return true;
        } catch (error) {
            if (!isQuotaExceededError(error)) return false;
        }

        // Quota is shared by every localStorage cache. Remove expired entries
        // first, then stale variants from this cache group, and retry once per
        // cleanup level. Cache persistence is optional and must never break UI.
        clearExpiredStoredKeys();
        try {
            localStorage.setItem(storageKey, storageValue);
            return true;
        } catch (error) {
            if (!isQuotaExceededError(error)) return false;
        }

        clearStoredGroup(group);
        try {
            localStorage.setItem(storageKey, storageValue);
            return true;
        } catch {
            return false;
        }
    },

    async getData(group, key, options = {}) {
        var hashKey = hash(key);
        const storageKey = `${CACHE_PREFIX}${group}-${hashKey}`;
        let cacheData = localStorage.getItem(storageKey);

        try {
            if (cacheData) cacheData = JSON.parse(cacheData);
            if (!cacheData) return null;
            const serializedData = await decodeCacheData(cacheData.data, cacheData.encoding);
            if (typeof serializedData !== 'string') {
                localStorage.removeItem(storageKey);
                return null;
            }
            const data = JSON.parse(serializedData);
            const expireAt = Number(cacheData.expire_at);
            // Records written by an older cache implementation did not always
            // carry an expiry. Treat those as stale once so they are rebuilt,
            // rather than becoming an unintended permanent catalogue snapshot.
            const expired = !Number.isFinite(expireAt) || expireAt <= 0 || expireAt < AntTime.now().unix();
            return options.includeMeta ? { data, expired, expireAt } : data;
        } catch {
            localStorage.removeItem(storageKey);
            return null;
        }
    },
    async clearAllCachData() {
        var keys = Object.keys(localStorage);

        for (let i = 0; i < keys.length; i++) {
            let key = keys[i];
            if (String(key).startsWith('CacheData-')) localStorage.removeItem(key);
        }
    },

    async clearByKey(group, key) {
        var keys = Object.keys(localStorage);

        if (key) {
            var hashKey = hash(key);
            for (let i = 0; i < keys.length; i++) {
                let strKey = keys[i];
                if (String(strKey).startsWith(`CacheData-${group}-${hashKey}`)) localStorage.removeItem(strKey);
            }
        } else {
            for (let i = 0; i < keys.length; i++) {
                let strKey = keys[i];
                if (String(strKey).startsWith(`CacheData-${group}`)) localStorage.removeItem(strKey);
            }
        }
    },

    clearExpiredKeys(group) {
        clearExpiredStoredKeys(group);
    },

    async clearGlobalExpiredKeys() {
        clearExpiredStoredKeys();
    }
};
